/*
 * Dashboard data layer: per-role module counts that mirror the list pages.
 *
 * Every chain below copies — role check for role check, in the same
 * precedence order and with the same quirks — the service selection and the
 * client-side row filters of the list page that the sidebar route renders
 * (see src/App.tsx). The goal is that `total` equals the number of rows the
 * page's default ("All") tab shows for the same user with no search, and that
 * every `byStatus` entry equals what the page shows when that tab is clicked.
 *
 * API notes (deviations from the requested interface: none). Additional
 * conventions:
 *  - `StatusCount.label` is always filled. For tabs the page renders through
 *    t(key) it holds the page's `defaultValue` (or an English fallback when the
 *    page has none) and `labelKey` holds the i18n key; for tabs the page shows
 *    as literal text, `labelKey` is omitted.
 *  - Errors are rethrown as plain `Error`s (message via utils/helper
 *    errorHandler) so the caller can catch per module. The one exception is the
 *    investigation-files "No investigation files found for this police
 *    station." response, which the page itself treats as an empty list
 *    (InvestigationFileList.tsx:331-337), so it is returned as 0 here too.
 *  - Page size: server-paginated endpoints are asked for page 1 with
 *    RECENT_LIMIT (6) rows; `count` does not depend on the page size. The
 *    appeal lists are the exception: they decide "server paginated or not"
 *    with `res.count > res.results.length` (ProsecutorApealList.tsx:201), a
 *    test whose outcome depends on page_size, so they are requested with the
 *    page's own PAGE_SIZE to reproduce the page's count exactly.
 */
import {
  AppealListParams,
  fetchAppealForClerk,
  fetchAppealForCoordinatorWithType,
  fetchAppealForRegistrar,
  fetchAppealForRequestingProsecutor,
  fetchAppealRequest,
  fetchDefendantAppealRequest,
  fetchDefendantAppealRequestByAdminCoordinator,
  fetchDefendantAppealRequestByAssignedProsecutor,
} from "services/appealService";
import {
  getAssetAppealsByAdminUnit,
  getAssetAppealsByProsecutor,
  getRespondentAssetAppealsByAdminUnit,
  getRespondentAssetAppealsByProsecutor,
} from "services/assetAppealService";
import {
  fetchAssetReferralByProsecutorId,
  fetchAssetReferralsByUnit,
  fetchReferralsByAdminUnit,
} from "services/assetRecoveryService";
import {
  fetchBailRight,
  fetchBailRightByAssignedProsecutor,
  fetchBailRightByRole,
} from "services/bailRightService";
import {
  CaseListParams,
  fetchCaseCreation,
  fetchCaseCreationByCoordinatorWithType,
  fetchCaseCreationByProsecutorId,
  fetchCaseCreationByRegistrarWithType,
  fetchUpcomingHearings,
  HearingType,
} from "services/caseServices";
import {
  fetchCrimeReferencesByCoordinatorRolePaginated,
  fetchCrimeReferencesPaginated,
} from "services/incidentService";
import {
  fetchFurtherInvestigation,
  fetchFurtherInvestigationByAdminUnitAndRole,
  fetchReceivingInvestigationFileByCoordinatorPaginated,
  fetchReceivingInvestigationFileByCoordinatorWithTypePaginated,
  fetchReceivingInvestigationFileByPolicePaginated,
  fetchReceivingInvestigationFileByProsecutorPaginated,
  fetchReceivingInvestigationFilePaginated,
  fetchScheduleForCoordinator,
  fetchScheduleForPolice,
  ListParams as InvestigationListParams,
} from "services/prosecutorPoliceService";
import {
  fetchWitnessProction,
  fetchWitnessProctionByAssignedProsecutor,
  fetchWitnessProctionForCoordinator,
  fetchWitnessProctionForRegistrar,
} from "services/witnessProtectionService";
import {
  AppealRequestStatus,
  AppealRequestType,
  CaseStatus,
  CaseStatusFilter,
  CrimeType,
  DefendantAppealStatus,
  IncidentStatus,
  LoginAccount,
  Roles,
} from "types";
import { errorHandler, hasRole } from "utils/helper";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type ModuleKey =
  | "incidents"
  | "investigationFiles"
  | "furtherInvestigation"
  | "charges"
  | "upcomingHearings"
  | "prosecutorAppeal"
  | "cassationAppeal"
  | "defendantAppeal"
  | "defendantCassation"
  | "bailRight"
  | "witnessProtection"
  | "schedules"
  | "assetRecovery"
  | "assetAppeal"
  | "assetAppealRespondent";

// One per tab the page shows for this role, excluding "All". label = the
// literal text the page shows if it is not translated, labelKey = the i18n key
// if the page uses t(key).
export interface StatusCount {
  key: string;
  label: string;
  labelKey?: string;
  count: number;
}

// route = where clicking the row goes on that page (the page's navigate
// target), or the list route if the page has no per-row navigation.
export interface RecentRow {
  id: string;
  title: string;
  subtitle?: string;
  status?: string | null;
  date?: string | null;
  route: string;
}

export interface ModuleCount {
  key: ModuleKey;
  route: string;
  total: number;
  byStatus: StatusCount[];
  recent: RecentRow[];
}

export const MODULE_ROUTES: Record<ModuleKey, string> = {
  incidents: "/incident-list",
  investigationFiles: "/investigation-files",
  furtherInvestigation: "/further-investigation",
  charges: "/charges",
  upcomingHearings: "/upcoming-hearings",
  prosecutorAppeal: "/prosecutor-appeal",
  cassationAppeal: "/cassation-appeal",
  defendantAppeal: "/defendant-appeal",
  defendantCassation: "/defendant-cassation",
  bailRight: "/bail-right",
  witnessProtection: "/witness-protection",
  schedules: "/schedules",
  assetRecovery: "/asset-recovery",
  assetAppeal: "/asset-appeal",
  assetAppealRespondent: "/asset-appeal-respondent",
};

// ---------------------------------------------------------------------------
// Sidebar mirror
// ---------------------------------------------------------------------------

// The sidebar's single "appeal" menu item renders AppealDropdown (prosecutor,
// defendant) followed by CassationDropdown (prosecutor, defendant)
// (Sidebar.tsx:160-237, :825-832).
const APPEAL_ITEMS: ModuleKey[] = [
  "prosecutorAppeal",
  "defendantAppeal",
  "cassationAppeal",
  "defendantCassation",
];
// Sidebar.tsx:239 incidentMenu
const INCIDENT_MENU: ModuleKey[] = ["incidents"];
// Sidebar.tsx:246 prosecutorMenus
const PROSECUTOR_MENUS: ModuleKey[] = [
  "investigationFiles",
  "charges",
  "upcomingHearings",
  ...APPEAL_ITEMS,
  "bailRight",
  "witnessProtection",
];
// Sidebar.tsx:289 appealMenu
const APPEAL_MENU: ModuleKey[] = [...APPEAL_ITEMS];
// Sidebar.tsx:298 coordinatorMenus
const COORDINATOR_MENUS: ModuleKey[] = [
  "investigationFiles",
  "charges",
  "upcomingHearings",
  ...APPEAL_ITEMS,
  "bailRight",
  "witnessProtection",
  "schedules",
];
// Sidebar.tsx:347 registrarMenus
const REGISTRAR_MENUS: ModuleKey[] = [
  "investigationFiles",
  "charges",
  "upcomingHearings",
  ...APPEAL_ITEMS,
  "assetRecovery",
  "assetAppeal",
  "assetAppealRespondent",
  "bailRight",
  "witnessProtection",
  "schedules",
];
// Sidebar.tsx:444 clerkMenus
const CLERK_MENUS: ModuleKey[] = [
  "investigationFiles",
  "furtherInvestigation",
  "charges",
];
// Sidebar.tsx:465 screeningCoordinatorMenus
const SCREENING_COORDINATOR_MENUS: ModuleKey[] = ["incidents"];
// Sidebar.tsx:474 policeMenus. Its first item (/economic-coruption-investigation,
// InvestigationList) has no ModuleKey and is intentionally not represented.
const POLICE_MENUS: ModuleKey[] = [
  "investigationFiles",
  "furtherInvestigation",
  "schedules",
];
// Sidebar.tsx:501 deputyMenus
const DEPUTY_MENUS: ModuleKey[] = [
  "investigationFiles",
  "charges",
  "bailRight",
  "witnessProtection",
  "schedules",
];
// Sidebar.tsx:539 assetProsecutorMenus / :566 assetCoordinatorMenus
const ASSET_PROSECUTOR_MENUS: ModuleKey[] = [
  "assetRecovery",
  "assetAppeal",
  "assetAppealRespondent",
];
const ASSET_COORDINATOR_MENUS: ModuleKey[] = [
  "assetRecovery",
  "assetAppeal",
  "assetAppealRespondent",
];

export function visibleModules(loginData: any): ModuleKey[] {
  let selectedMenus: ModuleKey[] = [];

  // Mirrors Sidebar.tsx:655-741 (if/else chain), verbatim role checks.
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);

  if (isCorruptionAppealCoordinator) {
    selectedMenus = APPEAL_MENU;
  } else if (
    hasRole(loginData?.roles, [
      Roles.economicRegistrar, // "CaseCorruptionRegistrar"
      Roles.corruptionRegistrar, // "CaseEconomicRegistrar"
    ])
  ) {
    selectedMenus = [...INCIDENT_MENU, ...REGISTRAR_MENUS];
  } else if (hasRole(loginData?.roles, [Roles.registrar])) {
    selectedMenus = REGISTRAR_MENUS;
  } else if (
    hasRole(loginData?.roles, [
      Roles.appealProsecutor,
      loginData?.structure?.admin_unit?.parent_unit
        ? Roles.childAppealCoordinator
        : "",
    ])
  ) {
    selectedMenus = APPEAL_MENU;
  } else if (hasRole(loginData?.roles, [Roles.economicProsecutor])) {
    selectedMenus = [...INCIDENT_MENU, ...PROSECUTOR_MENUS];
  } else if (hasRole(loginData?.roles, [Roles.prosecutor])) {
    selectedMenus = PROSECUTOR_MENUS;
  } else if (
    hasRole(loginData?.roles, [
      Roles.coordinator,
      Roles.variousCoordinator,
      Roles.economicCoordinator,
      Roles.corruptionCoordinator,
      Roles.corruptionDirector,
    ])
  ) {
    selectedMenus = hasRole(loginData?.roles, [
      Roles.economicCoordinator,
      Roles.corruptionDirector,
    ])
      ? [...INCIDENT_MENU, ...COORDINATOR_MENUS]
      : COORDINATOR_MENUS;
  } else if (hasRole(loginData?.roles, [Roles.registrar])) {
    selectedMenus = REGISTRAR_MENUS;
  } else if (hasRole(loginData?.roles, [Roles.economicDirector])) {
    selectedMenus = COORDINATOR_MENUS;
  } else if (
    hasRole(loginData?.roles, [
      Roles.prosecutionDirector,
      Roles.economicDirector,
    ])
  ) {
    selectedMenus = COORDINATOR_MENUS;
  } else if (hasRole(loginData?.roles, [Roles.economicClerk])) {
    selectedMenus = [...INCIDENT_MENU, ...CLERK_MENUS];
  } else if (hasRole(loginData?.roles, [Roles.clerk, Roles.corruptionClerk])) {
    selectedMenus = CLERK_MENUS;
  } else if (hasRole(loginData?.roles, [Roles.screeningTeamCoordinator])) {
    selectedMenus = SCREENING_COORDINATOR_MENUS;
  } else if (
    // Quirk kept: with loginData passed, hasRole only recognises the pseudo
    // roles "Police"/"PoliceCoordinator", so this branch never matches.
    hasRole(loginData?.roles, [Roles.member], loginData as LoginAccount)
  ) {
    selectedMenus = POLICE_MENUS;
  } else if (hasRole(loginData?.roles, [Roles.deputy])) {
    selectedMenus = DEPUTY_MENUS;
  } else if (hasRole(loginData?.roles, [Roles.generalAttorney])) {
    selectedMenus = DEPUTY_MENUS;
  } else if (loginData?.role === "Member" || loginData?.is_police) {
    selectedMenus = POLICE_MENUS;
  } else if (
    hasRole(loginData?.roles, [
      Roles.appealCorruptionProsecutor,
      Roles.appealEconomicProsecutor,
      Roles.appealVariousProsecutor,
      Roles.variousAppealCoordinator,
      Roles.economicAppealCoordinator,
      Roles.corruptionAppealCoordinator,
      Roles.appealChildAndWomenCaseProsecutor,
    ])
  ) {
    selectedMenus = APPEAL_MENU;
  }

  // Overrides, Sidebar.tsx:743-786.
  if (
    hasRole(loginData?.roles, [Roles.assetCoordinator]) &&
    !isCorruptionAppealCoordinator
  ) {
    selectedMenus = ASSET_COORDINATOR_MENUS;
  }

  if (
    hasRole(loginData?.roles, [Roles.assetProsecutor]) &&
    !isCorruptionAppealCoordinator
  ) {
    selectedMenus = ASSET_PROSECUTOR_MENUS;
  }

  if (
    hasRole(loginData?.roles, [Roles.corruptionDirector]) &&
    !isCorruptionAppealCoordinator
  ) {
    selectedMenus = [
      ...selectedMenus,
      "assetRecovery",
      "assetAppeal",
      "assetAppealRespondent",
    ];
  }

  // The sidebar can render the asset items twice (asset role + corruption
  // director); the dashboard shows each module once, keeping first position.
  return selectedMenus.filter((k, i) => selectedMenus.indexOf(k) === i);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RECENT_LIMIT = 6;

const toError = (error: any): Error =>
  error instanceof Error && !(error as any)?.response && !(error as any)?.request
    ? error
    : new Error(errorHandler(error));

async function call<T>(p: Promise<T>): Promise<T> {
  try {
    return await p;
  } catch (error) {
    throw toError(error);
  }
}

const asArray = <T = any>(data: any): T[] =>
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];

const str = (v: any): string =>
  v === undefined || v === null ? "" : String(v);

const dateStr = (v: any): string | null => {
  if (v === undefined || v === null || v === "") return null;
  if (v instanceof Date) return v.toISOString();
  return String(v);
};

const make = (
  key: ModuleKey,
  total: number,
  byStatus: StatusCount[],
  recent: RecentRow[],
): ModuleCount => ({
  key,
  route: MODULE_ROUTES[key],
  total,
  byStatus,
  recent: recent.slice(0, RECENT_LIMIT),
});

// ---------------------------------------------------------------------------
// Incidents — IncidentList.tsx
// ---------------------------------------------------------------------------

// IncidentList.tsx:19-28 STATUS_FILTERS (labels are literal IncidentStatus text).
const INCIDENT_TABS: { label: string; value: string }[] = [
  { label: IncidentStatus.pending, value: "pending" },
  {
    label: IncidentStatus["forwarded to registrar"],
    value: "forwarded to registrar",
  },
  { label: IncidentStatus.sent_to_police, value: "sent_to_police" },
  { label: IncidentStatus.terminated, value: "terminated" },
];

async function loadIncidents(loginData: any): Promise<ModuleCount> {
  // Mirrors IncidentList.tsx:77-116.
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isScreeningCoordinator = hasRole(loginData?.roles, [
    Roles.screeningTeamCoordinator,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar, // "CaseCorruptionRegistrar"
    Roles.corruptionRegistrar, // "CaseEconomicRegistrar"
  ]);

  const coordinatorRole = isCorruptionDirector
    ? Roles.corruptionDirector
    : isCorruptionCoordinator
      ? Roles.corruptionCoordinator
      : isScreeningCoordinator
        ? Roles.screeningTeamCoordinator
        : isCorruptionRegistrar
          ? Roles.economicRegistrar // "CaseCorruptionRegistrar"
          : null;

  const fetchPage = (status?: string) => {
    const params = {
      page: 1,
      pageSize: RECENT_LIMIT,
      search: undefined,
      status: status || undefined,
    };
    return call(
      coordinatorRole
        ? fetchCrimeReferencesByCoordinatorRolePaginated(
            loginData?.admin_unit || "",
            coordinatorRole,
            params,
          )
        : fetchCrimeReferencesPaginated(params),
    );
  };

  const [all, ...tabs] = await Promise.all([
    fetchPage(),
    ...INCIDENT_TABS.map((tab) => fetchPage(tab.value)),
  ]);

  // Row click target: IncidentList.tsx:264-269.
  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const recent: RecentRow[] = (all.results || []).map((incident: any, i) => ({
    id: str(incident?.uu_id ?? i),
    title: str(incident?.Incident_Registration_number),
    subtitle: incident?.crime_type
      ? (CrimeType as Record<string, string>)[incident.crime_type] ||
        str(incident.crime_type)
      : undefined,
    status: incident?.status ?? null,
    date: dateStr(incident?.date_received),
    route: isClerk
      ? "/record-incident/" + incident?.uu_id
      : "/incident/" + incident?.uu_id,
  }));

  return make(
    "incidents",
    all.count || 0,
    INCIDENT_TABS.map((tab, i) => ({
      key: tab.value,
      label: tab.label,
      count: tabs[i].count || 0,
    })),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Investigation files — InvestigationFileList.tsx
// ---------------------------------------------------------------------------

// InvestigationFileList.tsx:128-141 statusFilters / statusDisplayNames. The
// tab text is the literal display name (statusDisplayNames[status] || t(status)
// always resolves to the literal).
const IF_STATUS_TABS: { key: string; label: string }[] = [
  { key: "forward_to_coordinator", label: "Pending" },
  { key: "assigned_to_prosecutor", label: "Assign to Prosecutor" },
  { key: "assigned_to_clerk", label: "Forward to Clerk" },
  { key: "terminated", label: "Terminated" },
  { key: "further_investigation_requested", label: "Further Investigation" },
];

const POLICE_EMPTY_MESSAGE =
  "No investigation files found for this police station.";

async function loadInvestigationFiles(loginData: any): Promise<ModuleCount> {
  // Mirrors InvestigationFileList.tsx:143-196 (role flags).
  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isVariousClerk = hasRole(loginData?.roles, [Roles.variousClerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);
  const isChildClerk = hasRole(loginData?.roles, [Roles.childWomenClerk]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isCoordinator = hasRole(loginData?.roles, [
    Roles.coordinator,
    Roles.economicCoordinator,
    Roles.corruptionCoordinator,
    Roles.variousCoordinator,
  ]);
  const isVarious = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
    Roles.variousDirector,
    Roles.variousRegistrar,
  ]);
  const isEconomic = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
    Roles.economicDirector,
    Roles.economicRegistrar,
  ]);
  const isCorruption = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
    Roles.corruptionDirector,
    Roles.corruptionRegistrar,
  ]);

  const isChildWomen = hasRole(loginData?.roles, [Roles.childWomenCoordinator]);

  const isZonalVariousChildClerk = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenClerk,
  ]);
  const isZonalEconomicCorruptionClerk = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionClerk,
  ]);
  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  // Literal role strings, as the page does (Roles keys are swapped in types.ts).
  const isVariousRegistrar = hasRole(loginData?.roles, ["CaseVariousRegistrar"]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    "CaseCorruptionRegistrar",
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    "CaseEconomicRegistrar",
  ]);

  // InvestigationFileList.tsx:199-209 — status tabs only for these roles.
  const showStatusFilters =
    isCoordinator || isVarious || isEconomic || isCorruption;

  // InvestigationFileList.tsx:238-248 — crime-type clerks are forced to
  // status=assigned_to_clerk regardless of the tab.
  const clerkOnlyAssigned =
    isVariousClerk || isEconomicClerk || isCorruptionClerk;

  const fetchPage = async (tabStatus?: string) => {
    const statusParam = clerkOnlyAssigned ? "assigned_to_clerk" : tabStatus;
    const adminUnit = loginData?.admin_unit || "";
    const orgUnit = loginData?.org_unit || "";
    // decisionFilter defaults to "" → decisionParams = {} (page :255-256).
    const params: InvestigationListParams = {
      page: 1,
      pageSize: RECENT_LIMIT,
      search: undefined,
      status: statusParam || undefined,
    };

    const byType = (role: string) =>
      fetchReceivingInvestigationFileByCoordinatorWithTypePaginated(
        adminUnit,
        role,
        params,
      );

    // Mirrors InvestigationFileList.tsx:271-330.
    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? byType(
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
          )
        : isVariousRegistrar
          ? byType(Roles.variousCoordinator)
          : isCorruptionRegistrar
            ? byType(Roles.corruptionCoordinator)
            : isEconomicRegistrar
              ? byType(Roles.economicCoordinator)
              : isClerk
                ? isZonalEconomicCorruptionClerk || isZonalVariousChildClerk
                  ? byType(
                      isZonalEconomicCorruptionClerk
                        ? Roles.zonalEconomicCoruptionClerk
                        : Roles.zonalVariousChildWomenClerk,
                    )
                  : isChildClerk
                    ? byType(Roles.childWomenClerk)
                    : byType(
                        isVariousClerk
                          ? Roles.variousClerk
                          : isEconomicClerk
                            ? Roles.economicClerk
                            : Roles.corruptionClerk,
                      )
                : isVarious
                  ? byType(Roles.variousCoordinator)
                  : isEconomic
                    ? byType(Roles.economicCoordinator)
                    : isCorruption
                      ? byType(Roles.corruptionCoordinator)
                      : isChildWomen
                        ? byType(Roles.childWomenCoordinator)
                        : isCoordinator
                          ? fetchReceivingInvestigationFileByCoordinatorPaginated(
                              adminUnit,
                              orgUnit,
                              params,
                            )
                          : isProsecutor
                            ? fetchReceivingInvestigationFileByProsecutorPaginated(
                                loginData?.user_id || "",
                                params,
                              )
                            : loginData?.is_police ||
                                loginData?.role === "Member"
                              ? fetchReceivingInvestigationFileByPolicePaginated(
                                  loginData?.organization?.uu_id || "",
                                  params,
                                )
                              : fetchReceivingInvestigationFilePaginated(params);

    try {
      return await api;
    } catch (error: any) {
      // The page shows an empty list for this police response (:331-337).
      if (error?.response?.data?.message === POLICE_EMPTY_MESSAGE) {
        return { count: 0, next: null, previous: null, results: [] as any[] };
      }
      throw toError(error);
    }
  };

  const tabDefs = showStatusFilters ? IF_STATUS_TABS : [];
  const [all, ...tabs] = await Promise.all([
    fetchPage(undefined),
    ...tabDefs.map((tab) => fetchPage(tab.key)),
  ]);

  // Row click target: InvestigationFileList.tsx:604-609.
  const recent: RecentRow[] = (all.results || []).map((file: any, i) => ({
    id: str(file?.id ?? i),
    title: str(file?.crime_reference_number),
    subtitle: file?.police_station_name || undefined,
    status: file?.status ?? null,
    date: dateStr(file?.receiving_date),
    route: "/investigation-files/" + file?.id?.toString(),
  }));

  return make(
    "investigationFiles",
    all.count || 0,
    tabDefs.map((tab, i) => ({
      key: tab.key,
      label: tab.label,
      count: tabs[i].count || 0,
    })),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Further investigation — FurtherInvestigationListForPolice.tsx (route
// /further-investigation, App.tsx:396)
// ---------------------------------------------------------------------------

async function loadFurtherInvestigation(loginData: any): Promise<ModuleCount> {
  // Mirrors FurtherInvestigationListForPolice.tsx:28-48.
  const isZonalVariousChildClerk = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenClerk,
  ]);
  const isZonalEconomicCorruptionClerk = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionClerk,
  ]);

  const data = await call(
    isZonalVariousChildClerk || isZonalEconomicCorruptionClerk
      ? fetchFurtherInvestigationByAdminUnitAndRole(
          loginData?.admin_unit_id,
          isZonalVariousChildClerk
            ? Roles.zonalVariousChildWomenClerk
            : Roles.zonalEconomicCoruptionClerk,
        )
      : fetchFurtherInvestigation(),
  );

  // The page uses the response as an array directly (it would crash on an
  // envelope); asArray tolerates both. The search filter (:63-84) with an
  // empty term keeps every row. No tabs (tabs={[]}, :106).
  const rows = asArray(data);

  // Row click target: FurtherInvestigationListForPolice.tsx:232-234.
  const recent: RecentRow[] = rows.map((fi: any, i) => ({
    id: str(fi?.id ?? i),
    title: str(fi?.crime_registration_number),
    subtitle: fi?.incident_registration_number || undefined,
    status: fi?.decision ?? fi?.status ?? null,
    date: dateStr(fi?.date_sent_to_police),
    route: "/further-investigation/" + fi?.id,
  }));

  return make("furtherInvestigation", rows.length, [], recent);
}

// ---------------------------------------------------------------------------
// Charges — InstituteOfChargeListForsidebar.tsx
// ---------------------------------------------------------------------------

// InstituteOfChargeListForsidebar.tsx:24-56 STATUS_FILTERS (literal labels).
const CHARGE_TABS: {
  label: string;
  value: string;
  field: "status" | "status_filter";
}[] = [
  {
    label: CaseStatus.forwarded_to_coordinator,
    value: "forwarded_to_coordinator",
    field: "status",
  },
  {
    label: CaseStatus.forwarded_to_registrar,
    value: "forwarded_to_registrar",
    field: "status",
  },
  { label: CaseStatus.sent_to_court, value: "sent_to_court", field: "status" },
  {
    label: CaseStatusFilter.final_verdict_given,
    value: "final_verdict_given",
    field: "status_filter",
  },
  {
    label: CaseStatusFilter.appealed,
    value: "appealed",
    field: "status_filter",
  },
  {
    label: CaseStatus.case_terminated_by_court,
    value: "case_terminated_by_court",
    field: "status",
  },
];

async function loadCharges(loginData: any): Promise<ModuleCount> {
  // Mirrors InstituteOfChargeListForsidebar.tsx:77-123 (role flags).
  const isVarious = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
    Roles.variousDirector,
    Roles.variousRegistrar,
  ]);
  const isEconomic = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
    Roles.economicDirector,
  ]);
  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);

  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isEconomicDirector = hasRole(loginData?.roles, [Roles.economicDirector]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);

  const isChildWomen = hasRole(loginData?.roles, [Roles.childWomenCoordinator]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isVariousClerk = hasRole(loginData?.roles, [Roles.variousClerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);
  const isChildWomenClerk = hasRole(loginData?.roles, [Roles.childWomenClerk]);

  const fetchPage = (tab?: (typeof CHARGE_TABS)[number]) => {
    const adminUnit = loginData?.admin_unit || "";
    const orgUnit = loginData?.org_unit || "";
    // InstituteOfChargeListForsidebar.tsx:131-139 status vs status_filter.
    const params: CaseListParams = {
      page: 1,
      pageSize: RECENT_LIMIT,
      search: undefined,
      status: tab?.field === "status" ? tab.value || undefined : undefined,
      status_filter:
        tab?.field === "status_filter" ? tab.value || undefined : undefined,
    };
    const byCoordinator = (role: string) =>
      fetchCaseCreationByCoordinatorWithType(adminUnit, orgUnit, role, params);
    const byRegistrar = (role: string) =>
      fetchCaseCreationByRegistrarWithType(adminUnit, role, params);

    // Mirrors InstituteOfChargeListForsidebar.tsx:159-197.
    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? byCoordinator(
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
          )
        : isVariousDirector
          ? byCoordinator(Roles.variousDirector)
          : isEconomicDirector
            ? byCoordinator(Roles.economicDirector)
            : isCorruptionDirector
              ? byCoordinator(Roles.corruptionDirector)
              : isVarious
                ? byCoordinator(Roles.variousCoordinator)
                : isEconomic
                  ? byCoordinator(Roles.economicCoordinator)
                  : isCorruptionCoordinator
                    ? byCoordinator(Roles.corruptionCoordinator)
                    : isChildWomen
                      ? byCoordinator(Roles.childWomenCoordinator)
                      : isVariousClerk
                        ? byCoordinator(Roles.variousClerk)
                        : isEconomicClerk
                          ? byCoordinator(Roles.economicClerk)
                          : isCorruptionClerk
                            ? byCoordinator(Roles.corruptionClerk)
                            : isChildWomenClerk
                              ? byCoordinator(Roles.childWomenClerk)
                              : isVariousRegistrar
                                ? byRegistrar(Roles.variousRegistrar)
                                : isEconomicRegistrar
                                  ? byRegistrar(Roles.economicRegistrar)
                                  : isCorruptionRegistrar
                                    ? byRegistrar(Roles.corruptionCoordinator)
                                    : isClerk
                                      ? byCoordinator(Roles.coordinator)
                                      : isProsecutor
                                        ? fetchCaseCreationByProsecutorId(
                                            loginData?.user_id || "",
                                            params,
                                          )
                                        : fetchCaseCreation(params);
    return call(api);
  };

  const [all, ...tabs] = await Promise.all([
    fetchPage(undefined),
    ...CHARGE_TABS.map((tab) => fetchPage(tab)),
  ]);

  // Status shown in the badge: InstituteOfChargeListForsidebar.tsx:313-326.
  // Row click target: navigate(charge.id) relative to /charges (:333).
  const recent: RecentRow[] = (all.results || []).map((charge: any, i) => ({
    id: str(charge?.id ?? i),
    title: str(charge?.prosecutor_registration_number),
    status: charge?.is_case_updated_by_court
      ? charge?.status || ""
      : charge?.status_filter && charge?.status_filter !== "on_charge_draft"
        ? charge.status_filter
        : charge?.status || "",
    date: dateStr(charge?.date_sent_to_prosecutor),
    route: "/charges/" + charge?.id?.toString(),
  }));

  return make(
    "charges",
    all.count || 0,
    CHARGE_TABS.map((tab, i) => ({
      key: tab.value,
      label: tab.label,
      count: tabs[i].count || 0,
    })),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Upcoming hearings — UpcomingHearingsList.tsx
// ---------------------------------------------------------------------------

// UpcomingHearingsList.tsx:21-26 + :109-115 (tabLabels via t()).
const HEARING_TABS: {
  key: string;
  type: HearingType;
  labelKey: string;
  label: string;
}[] = [
  { key: "charge", type: "charge", labelKey: "charge_hearing", label: "Charge Hearing" },
  { key: "appeal", type: "appeal", labelKey: "appeal_hearing", label: "Appeal Hearing" },
  { key: "bail", type: "bail", labelKey: "bail_hearings", label: "Bail Hearings" },
];

// UpcomingHearingsList.tsx:120-145 openHearing.
const hearingRoute = (h: any): string => {
  const cat = (h?.category || h?.source || "").toString().toLowerCase();
  if (cat.includes("bail")) {
    return `/bail-right/${h?.bail_right_id || h?.source_id || h?.case_id}`;
  } else if (cat.includes("cassation")) {
    return `/cassation-appeal/${
      h?.appeal_request_id || h?.source_id || h?.case_id
    }`;
  } else if (cat.includes("defendant")) {
    return `/defendant-appeal/${
      h?.defendant_appeal_request_id || h?.source_id || h?.case_id
    }`;
  } else if (cat.includes("appeal")) {
    return `/prosecutor-appeal/${
      h?.appeal_request_id || h?.source_id || h?.case_id
    }`;
  }
  return `/charges/${h?.case_id}`;
};

async function loadUpcomingHearings(loginData: any): Promise<ModuleCount> {
  // Mirrors UpcomingHearingsList.tsx:70-100.
  const roles = loginData?.roles;
  const isProsecutor = hasRole(roles, [Roles.prosecutor]);

  const coordinatorRole = (() => {
    if (hasRole(roles, [Roles.zonalVariousChildWomenRegistrar]))
      return Roles.zonalVariousChildWomenRegistrar;
    if (hasRole(roles, [Roles.zonalEconomicCoruptionRegistrsrar]))
      return Roles.zonalEconomicCoruptionRegistrsrar;
    if (hasRole(roles, [Roles.variousDirector])) return Roles.variousDirector;
    if (hasRole(roles, [Roles.economicDirector])) return Roles.economicDirector;
    if (hasRole(roles, [Roles.corruptionDirector]))
      return Roles.corruptionDirector;
    if (hasRole(roles, [Roles.variousCoordinator]))
      return Roles.variousCoordinator;
    if (hasRole(roles, [Roles.economicCoordinator]))
      return Roles.economicCoordinator;
    if (hasRole(roles, [Roles.corruptionCoordinator]))
      return Roles.corruptionCoordinator;
    if (hasRole(roles, [Roles.childWomenCoordinator]))
      return Roles.childWomenCoordinator;
    if (hasRole(roles, [Roles.variousClerk])) return Roles.variousClerk;
    if (hasRole(roles, [Roles.economicClerk])) return Roles.economicClerk;
    if (hasRole(roles, [Roles.corruptionClerk])) return Roles.corruptionClerk;
    if (hasRole(roles, [Roles.childWomenClerk])) return Roles.childWomenClerk;
    if (hasRole(roles, [Roles.variousRegistrar])) return Roles.variousRegistrar;
    if (hasRole(roles, [Roles.economicRegistrar]))
      return Roles.economicRegistrar;
    if (hasRole(roles, [Roles.corruptionRegistrar]))
      return Roles.corruptionCoordinator;
    if (hasRole(roles, [Roles.clerk])) return Roles.coordinator;
    return undefined;
  })();

  // Mirrors UpcomingHearingsList.tsx:152-159 scope.
  const scope = isProsecutor
    ? { assigned_prosecutor_userid: loginData?.user_id || "" }
    : coordinatorRole
      ? {
          coordinator_role: coordinatorRole,
          admin_unit_id: loginData?.admin_unit || "",
        }
      : {};

  // Mirrors UpcomingHearingsList.tsx:161-168.
  const fetchPage = (hearingType?: HearingType) =>
    call(
      fetchUpcomingHearings({
        hearing_type: hearingType,
        ...scope,
        page: 1,
        pageSize: RECENT_LIMIT,
      }),
    );

  const [all, ...tabs] = await Promise.all([
    fetchPage(undefined),
    ...HEARING_TABS.map((tab) => fetchPage(tab.type)),
  ]);

  // The endpoint returns future-dated hearings, soonest first.
  const recent: RecentRow[] = (all.results || []).map((h: any, i) => ({
    id: str(h?.id ?? i),
    title: h?.registration_number || "-",
    subtitle:
      [h?.court_type, h?.court_level].filter(Boolean).join(" ") ||
      h?.category ||
      undefined,
    status: h?.category ?? null,
    date: dateStr(h?.appointment_date),
    route: hearingRoute(h),
  }));

  return make(
    "upcomingHearings",
    all.count || 0,
    HEARING_TABS.map((tab, i) => ({
      key: tab.key,
      label: tab.label,
      labelKey: tab.labelKey,
      count: tabs[i].count || 0,
    })),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Appeals — shared result handling
// ---------------------------------------------------------------------------

type AppealPage = {
  count: number;
  next: string | null;
  previous: string | null;
  results: any[];
};

// Mirrors the .then() of ProsecutorApealList.tsx:199-238 /
// CassationApealList.tsx:205-243 / DefendantApealList.tsx:191-231 with an
// empty search and page 1.
const resolveAppealPage = (
  res: AppealPage,
  scopeFilter: (a: any) => boolean,
  selectedStatus?: string | null,
): { count: number; rows: any[] } => {
  const serverPaginated =
    res.next != null || res.previous != null || res.count > res.results.length;
  const scoped = (res.results || []).filter(scopeFilter);
  if (serverPaginated) return { count: res.count, rows: scoped };
  let rows = scoped;
  if (selectedStatus) rows = rows.filter((a) => a.status === selectedStatus);
  return { count: rows.length, rows };
};

// ---------------------------------------------------------------------------
// Prosecutor appeal — ProsecutorApealList.tsx
// ---------------------------------------------------------------------------

const PROSECUTOR_APPEAL_PAGE_SIZE = 9; // ProsecutorApealList.tsx:27
// ProsecutorApealList.tsx:267-283 (t(`status_${key}`) with AppealRequestStatus default).
const PROSECUTOR_APPEAL_TAB_KEYS: (keyof typeof AppealRequestStatus)[] = [
  "PENDING",
  "ASSIGNED_TO_PROSECUTOR",
  "SUBMITTED_TO_COURT",
  "COMPLETED",
];

const appealRow = (route: string) => (a: any, i: number): RecentRow => ({
  id: str(a?.id ?? i),
  title: str(
    a?.crime_registration_number ||
      a?.court_registration_number ||
      a?.incident_registration_number,
  ),
  subtitle: a?.appeal_type
    ? (AppealRequestType as Record<string, string>)[a.appeal_type] ||
      str(a.appeal_type)
    : undefined,
  status: a?.status ?? null,
  date: dateStr(a?.created_at),
  route: `${route}/${a?.id}`,
});

async function loadProsecutorAppeal(loginData: any): Promise<ModuleCount> {
  // Mirrors ProsecutorApealList.tsx:56-144 (role flags + selectedAdminRole).
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isVariousClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);

  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isVariousAppealCoordinator = hasRole(loginData?.roles, [
    Roles.variousAppealCoordinator,
  ]);
  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isEconomicAppealCoordinator = hasRole(loginData?.roles, [
    Roles.economicAppealCoordinator,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);
  const isChildWomenCoordinator = hasRole(loginData?.roles, [
    Roles.childWomenCoordinator,
  ]);
  const isChildAppealCoordinator = hasRole(loginData?.roles, [
    Roles.childAppealCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : isVariousDirector
      ? Roles.variousAppealCoordinator
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isVariousAppealCoordinator
          ? Roles.variousAppealCoordinator
          : isEconomicCoordinator
            ? Roles.economicCoordinator
            : isEconomicDirector
              ? Roles.economicAppealCoordinator
              : isEconomicRegistrar
                ? Roles.economicRegistrar
                : isEconomicAppealCoordinator
                  ? Roles.economicAppealCoordinator
                  : isCorruptionCoordinator
                    ? Roles.corruptionCoordinator
                    : isCorruptionDirector
                      ? Roles.corruptionAppealCoordinator
                      : isCorruptionRegistrar
                        ? Roles.corruptionRegistrar
                        : isCorruptionAppealCoordinator
                          ? Roles.corruptionAppealCoordinator
                          : isChildWomenCoordinator
                            ? Roles.childWomenCoordinator
                            : isChildAppealCoordinator
                              ? Roles.childAppealCoordinator
                              : null;

  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const fetchPage = async (status?: string) => {
    // ProsecutorApealList.tsx:150-157.
    const params: AppealListParams = {
      status: status || undefined,
      search: undefined,
      page: 1,
      pageSize: PROSECUTOR_APPEAL_PAGE_SIZE,
      is_cassation: false,
    };

    // Mirrors ProsecutorApealList.tsx:159-197.
    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? fetchAppealForCoordinatorWithType(
            loginData?.admin_unit + "",
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
            params,
          )
        : isProsecutor
          ? fetchAppealForRequestingProsecutor(loginData?.user_id + "", params)
          : selectedAdminRole
            ? fetchAppealForCoordinatorWithType(
                loginData?.admin_unit + "",
                selectedAdminRole,
                params,
              )
            : isVariousClerk
              ? fetchAppealForClerk(
                  loginData?.admin_unit + "",
                  Roles.variousClerk,
                  params,
                )
              : isEconomicClerk
                ? fetchAppealForClerk(
                    loginData?.admin_unit + "",
                    Roles.economicClerk,
                    params,
                  )
                : isCorruptionClerk
                  ? fetchAppealForClerk(
                      loginData?.admin_unit + "",
                      Roles.corruptionClerk,
                      params,
                    )
                  : isRegistrar
                    ? fetchAppealForRegistrar(loginData?.admin_unit + "", params)
                    : fetchAppealRequest(params);

    const res = (await call(api)) as AppealPage;
    // ProsecutorApealList.tsx:207-209 — this list excludes cassations.
    return resolveAppealPage(res, (a) => !a?.is_cassation, status);
  };

  const [all, ...tabs] = await Promise.all([
    fetchPage(undefined),
    ...PROSECUTOR_APPEAL_TAB_KEYS.map((key) => fetchPage(key)),
  ]);

  return make(
    "prosecutorAppeal",
    all.count,
    PROSECUTOR_APPEAL_TAB_KEYS.map((key, i) => ({
      key,
      label: AppealRequestStatus[key],
      labelKey: `status_${key.toLowerCase()}`,
      count: tabs[i].count,
    })),
    // Row click target: ProsecutorApealList.tsx:395.
    all.rows.map(appealRow("/prosecutor-appeal")),
  );
}

// ---------------------------------------------------------------------------
// Cassation appeal — CassationApealList.tsx
// ---------------------------------------------------------------------------

const CASSATION_PAGE_SIZE = 20; // CassationApealList.tsx:56
// CassationApealList.tsx:136-150 (t(`appeal_request_status_${key}`) with default).
const CASSATION_TAB_KEYS: (keyof typeof AppealRequestStatus)[] = [
  "PENDING",
  "ASSIGNED_TO_PROSECUTOR",
  "SUBMITTED_TO_COURT",
  "COMPLETED",
];

async function loadCassationAppeal(loginData: any): Promise<ModuleCount> {
  // Mirrors CassationApealList.tsx:54-133 (role flags + selectedAdminRole;
  // note: no child/women coordinators here, unlike the appeal list).
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isVariousClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);

  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isVariousAppealCoordinator = hasRole(loginData?.roles, [
    Roles.variousAppealCoordinator,
  ]);
  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isEconomicAppealCoordinator = hasRole(loginData?.roles, [
    Roles.economicAppealCoordinator,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : isVariousDirector
      ? Roles.variousAppealCoordinator
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isVariousAppealCoordinator
          ? Roles.variousAppealCoordinator
          : isEconomicCoordinator
            ? Roles.economicCoordinator
            : isEconomicDirector
              ? Roles.economicAppealCoordinator
              : isEconomicRegistrar
                ? Roles.economicRegistrar
                : isEconomicAppealCoordinator
                  ? Roles.economicAppealCoordinator
                  : isCorruptionCoordinator
                    ? Roles.corruptionCoordinator
                    : isCorruptionDirector
                      ? Roles.corruptionAppealCoordinator
                      : isCorruptionRegistrar
                        ? Roles.corruptionRegistrar
                        : isCorruptionAppealCoordinator
                          ? Roles.corruptionAppealCoordinator
                          : null;

  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const fetchPage = async (status?: string) => {
    // CassationApealList.tsx:155-164.
    const params: AppealListParams = {
      status: status || undefined,
      search: undefined,
      page: 1,
      pageSize: CASSATION_PAGE_SIZE,
      is_cassation: true,
    };

    // Mirrors CassationApealList.tsx:166-203 (registrar checked before
    // selectedAdminRole here, unlike the appeal list).
    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? fetchAppealForCoordinatorWithType(
            loginData?.admin_unit + "",
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
            params,
          )
        : isProsecutor
          ? fetchAppealForRequestingProsecutor(loginData?.user_id + "", params)
          : isRegistrar
            ? fetchAppealForRegistrar(loginData?.admin_unit + "", params)
            : selectedAdminRole
              ? fetchAppealForCoordinatorWithType(
                  loginData?.admin_unit + "",
                  selectedAdminRole,
                  params,
                )
              : isEconomicClerk
                ? fetchAppealForClerk(
                    loginData?.admin_unit + "",
                    Roles.economicClerk,
                    params,
                  )
                : isCorruptionClerk
                  ? fetchAppealForClerk(
                      loginData?.admin_unit + "",
                      Roles.corruptionClerk,
                      params,
                    )
                  : isVariousClerk
                    ? fetchAppealForClerk(
                        loginData?.admin_unit + "",
                        Roles.clerk,
                        params,
                      )
                    : fetchAppealRequest(params);

    const res = (await call(api)) as AppealPage;
    // CassationApealList.tsx:212-214 — only cassations.
    return resolveAppealPage(res, (a) => !!a?.is_cassation, status);
  };

  const [all, ...tabs] = await Promise.all([
    fetchPage(undefined),
    ...CASSATION_TAB_KEYS.map((key) => fetchPage(key)),
  ]);

  return make(
    "cassationAppeal",
    all.count,
    CASSATION_TAB_KEYS.map((key, i) => ({
      key,
      label: AppealRequestStatus[key],
      labelKey: `appeal_request_status_${key.toLowerCase()}`,
      count: tabs[i].count,
    })),
    // Row click target: CassationApealList.tsx:380.
    all.rows.map(appealRow("/cassation-appeal")),
  );
}

// ---------------------------------------------------------------------------
// Defendant appeal / defendant cassation — DefendantApealList.tsx
// (/defendant-cassation renders <DefendantAppealList isCassation />, App.tsx:443-446)
// ---------------------------------------------------------------------------

const DEFENDANT_PAGE_SIZE = 20; // DefendantApealList.tsx:68
// DefendantApealList.tsx:149-162 (t(`defendant_appeal_status_${key}`) with default).
const DEFENDANT_TAB_KEYS: (keyof typeof DefendantAppealStatus)[] = [
  "PENDING",
  "ASSIGNED_TO_PROSECUTOR",
  "FORWARDED_TO_COURT",
  "COMPLETED",
];

async function loadDefendantAppeal(
  loginData: any,
  isCassation: boolean | undefined,
): Promise<ModuleCount> {
  // Mirrors DefendantApealList.tsx:70-146 (selectedAdminRole).
  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isVariousAppealCoordinator = hasRole(loginData?.roles, [
    Roles.variousAppealCoordinator,
  ]);
  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isEconomicAppealCoordinator = hasRole(loginData?.roles, [
    Roles.economicAppealCoordinator,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);
  const isChildWomenCoordinator = hasRole(loginData?.roles, [
    Roles.childWomenCoordinator,
  ]);
  const isChildAppealCoordinator = hasRole(loginData?.roles, [
    Roles.childAppealCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : isVariousDirector
      ? Roles.variousAppealCoordinator
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isVariousAppealCoordinator
          ? Roles.variousAppealCoordinator
          : isEconomicCoordinator
            ? Roles.economicCoordinator
            : isEconomicDirector
              ? Roles.economicAppealCoordinator
              : isEconomicRegistrar
                ? Roles.economicRegistrar
                : isEconomicAppealCoordinator
                  ? Roles.economicAppealCoordinator
                  : isCorruptionCoordinator
                    ? Roles.corruptionCoordinator
                    : isCorruptionDirector
                      ? Roles.corruptionAppealCoordinator
                      : isCorruptionRegistrar
                        ? Roles.corruptionRegistrar
                        : isCorruptionAppealCoordinator
                          ? Roles.corruptionAppealCoordinator
                          : isChildWomenCoordinator
                            ? Roles.childWomenCoordinator
                            : isChildAppealCoordinator
                              ? Roles.childAppealCoordinator
                              : null;

  const fetchPage = async (status?: string) => {
    // DefendantApealList.tsx:168-176. is_casations is `undefined` (omitted)
    // on the appeal route, `true` on the cassation route.
    const params: AppealListParams = {
      status: status || undefined,
      search: undefined,
      page: 1,
      pageSize: DEFENDANT_PAGE_SIZE,
      is_casations: isCassation,
    };

    // Mirrors DefendantApealList.tsx:178-190. NOTE: the prosecutor branch
    // calls /appeal/defendant-appeal-requests/by-assigned-prosecutor/, which
    // the backend may not expose; mirrored as-is (a failure surfaces as an
    // Error for this module, where the page would show an empty list).
    const api = hasRole(loginData?.roles, [Roles.prosecutor])
      ? fetchDefendantAppealRequestByAssignedProsecutor(
          loginData?.user_id + "",
          params,
        )
      : selectedAdminRole
        ? fetchDefendantAppealRequestByAdminCoordinator(
            loginData?.admin_unit + "",
            selectedAdminRole,
            params,
          )
        : fetchDefendantAppealRequest(params);

    const res = (await call(api)) as AppealPage;
    // DefendantApealList.tsx:198-202 — is_casations scoping.
    return resolveAppealPage(
      res,
      (appeal) => (isCassation ? !!appeal?.is_casations : !appeal?.is_casations),
      status,
    );
  };

  const [all, ...tabs] = await Promise.all([
    fetchPage(undefined),
    ...DEFENDANT_TAB_KEYS.map((key) => fetchPage(key)),
  ]);

  const key: ModuleKey = isCassation ? "defendantCassation" : "defendantAppeal";
  // Row click target: DefendantApealList.tsx:389-394.
  const base = isCassation ? "/defendant-cassation" : "/defendant-appeal";
  const recent: RecentRow[] = all.rows.map((a: any, i: number) => ({
    id: str(a?.id ?? i),
    title: str(a?.court_appeal_number || a?.higher_court_type),
    subtitle:
      [a?.higher_court_type, a?.court_zone, a?.court_woreda]
        .filter(Boolean)
        .join(" · ") || undefined,
    status: a?.status ?? null,
    date: dateStr(a?.created_at ?? a?.registrar_date),
    route: `${base}/${a?.id}`,
  }));

  return make(
    key,
    all.count,
    DEFENDANT_TAB_KEYS.map((k, i) => ({
      key: k,
      label: DefendantAppealStatus[k],
      labelKey: `defendant_appeal_status_${k.toLowerCase()}`,
      count: tabs[i].count,
    })),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Bail right — BailRightList.tsx
// ---------------------------------------------------------------------------

// BailRightList.tsx:86 tabs + :148-174 getStatusMatchForTab.
const BAIL_TABS: {
  key: string;
  label: string;
  labelKey?: string;
  match: (bail: any) => boolean;
}[] = [
  {
    key: "new_request",
    label: "New Request",
    labelKey: "new_request",
    match: (bail) => {
      const status = bail?.status?.toString() || "";
      return status === "forwarded_to_coordinator" && !bail?.prosecutor_summary;
    },
  },
  {
    key: "on_hearing",
    label: "On Hearing", // literal (untranslated) in the page
    match: (bail) => {
      const status = bail?.status?.toString() || "";
      const hasFinalDecision = (bail?.hearing_decisions || []).some(
        (h: any) => h?.if_final_decision_given === true,
      );
      return (
        status === "assigned_to_prosecutor" &&
        !!bail?.reference_number &&
        !hasFinalDecision
      );
    },
  },
  {
    key: "closed",
    label: "Closed",
    labelKey: "status_closed",
    match: (bail) => {
      const status = bail?.status?.toString() || "";
      const hasFinalDecision = (bail?.hearing_decisions || []).some(
        (h: any) => h?.if_final_decision_given === true,
      );
      return (
        hasFinalDecision ||
        status === "bail_rejected" ||
        status === "bail_granted"
      );
    },
  },
];

async function loadBailRight(loginData: any): Promise<ModuleCount> {
  // Mirrors BailRightList.tsx:32-84 (selectedAdminRole).
  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);
  const isChildWomenCoordinator = hasRole(loginData?.roles, [
    Roles.childWomenCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : isVariousDirector
      ? Roles.variousDirector
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isEconomicCoordinator
          ? Roles.economicCoordinator
          : isEconomicDirector
            ? Roles.economicDirector
            : isEconomicRegistrar
              ? Roles.economicRegistrar
              : isCorruptionCoordinator
                ? Roles.corruptionCoordinator
                : isCorruptionDirector
                  ? Roles.corruptionDirector
                  : isCorruptionRegistrar
                    ? Roles.corruptionRegistrar
                    : isChildWomenCoordinator
                      ? Roles.childWomenCoordinator
                      : null;

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  // Mirrors BailRightList.tsx:107-121.
  const data = await call(
    isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
      ? fetchBailRightByRole(
          loginData?.admin_unit || "",
          isZonalVariousChildRegistrar
            ? Roles.zonalVariousChildWomenRegistrar
            : Roles.zonalEconomicCoruptionRegistrsrar,
        )
      : isProsecutor
        ? fetchBailRightByAssignedProsecutor(loginData?.user_id || "")
        : selectedAdminRole
          ? fetchBailRightByRole(loginData?.admin_unit || "", selectedAdminRole)
          : fetchBailRight(),
  );

  // BailRightList.tsx:124-131 — newest created_at first.
  const rows = asArray(data).sort((a: any, b: any) => {
    const dateA = new Date(a.created_at || "").getTime();
    const dateB = new Date(b.created_at || "").getTime();
    return dateB - dateA;
  });
  // Default tab is t("all") → getStatusMatchForTab returns true for every row;
  // the empty-search text filter (:177-201) also keeps every row.

  // Row click target: BailRightList.tsx:309.
  const recent: RecentRow[] = rows.map((bail: any, i) => ({
    id: str(bail?.uu_id ?? i),
    title: str(
      bail?.suspect_fullname ||
        [
          bail?.suspect_info?.first_name,
          bail?.suspect_info?.middle_name,
          bail?.suspect_info?.last_name,
        ]
          .filter(Boolean)
          .join(" "),
    ),
    subtitle:
      [bail?.court_registration_number, bail?.court_name]
        .filter(Boolean)
        .join(" · ") || undefined,
    status: bail?.status ?? null,
    date: dateStr(bail?.created_at),
    route: "/bail-right/" + bail?.uu_id + "/",
  }));

  return make(
    "bailRight",
    rows.length,
    BAIL_TABS.map((tab) => ({
      key: tab.key,
      label: tab.label,
      ...(tab.labelKey ? { labelKey: tab.labelKey } : {}),
      count: rows.filter(tab.match).length,
    })),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Witness protection — WitnessProtectionList.tsx
// ---------------------------------------------------------------------------

async function loadWitnessProtection(loginData: any): Promise<ModuleCount> {
  // Mirrors WitnessProtectionList.tsx:160-243.
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isChildWomen = hasRole(loginData?.roles, [Roles.childWomenCoordinator]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [Roles.economicDirector]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const data = await call(
    isProsecutor
      ? fetchWitnessProctionByAssignedProsecutor(loginData?.user_id || "")
      : isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? fetchWitnessProctionForCoordinator(
            loginData?.admin_unit + "" || "",
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
          )
        : isVariousCoordinator
          ? fetchWitnessProctionForCoordinator(
              loginData?.admin_unit?.toString() || "",
              Roles.variousCoordinator,
            )
          : isEconomicCoordinator
            ? fetchWitnessProctionForCoordinator(
                loginData?.admin_unit?.toString() || "",
                Roles.economicCoordinator,
              )
            : isCorruptionCoordinator
              ? fetchWitnessProctionForCoordinator(
                  loginData?.admin_unit?.toString() || "",
                  Roles.corruptionCoordinator,
                )
              : isChildWomen
                ? fetchWitnessProctionForCoordinator(
                    loginData?.admin_unit?.toString() || "",
                    Roles.childWomenCoordinator,
                  )
                : isCorruptionDirector
                  ? fetchWitnessProctionForCoordinator(
                      loginData?.admin_unit?.toString() || "",
                      Roles.corruptionCoordinator,
                    )
                  : isEconomicDirector
                    ? fetchWitnessProctionForCoordinator(
                        loginData?.admin_unit?.toString() || "",
                        Roles.economicCoordinator,
                      )
                    : isVariousDirector
                      ? fetchWitnessProctionForCoordinator(
                          loginData?.admin_unit?.toString() || "",
                          Roles.variousCoordinator,
                        )
                      : isRegistrar
                        ? fetchWitnessProctionForRegistrar(
                            loginData?.admin_unit?.toString() || "",
                          )
                        : fetchWitnessProction(),
  );

  // Empty-search filter (:255-270) keeps every row; the page has no tabs.
  const rows = asArray(data);

  // Row click target: WitnessProtectionList.tsx:385-387.
  const recent: RecentRow[] = rows.map((p: any, i) => ({
    id: str(p?.id ?? i),
    title: str(p?.applicant_full_name),
    subtitle: p?.court_registration_number || undefined,
    status: p?.decision_status || p?.status || null,
    date: dateStr(p?.created_at),
    route: "/witness-protection/" + p?.id + "/",
  }));

  return make("witnessProtection", rows.length, [], recent);
}

// ---------------------------------------------------------------------------
// Schedules — Schedule/ScheduleList.tsx
// ---------------------------------------------------------------------------

async function loadSchedules(loginData: any): Promise<ModuleCount> {
  // Mirrors ScheduleList.tsx:35-41.
  const data = await call(
    loginData?.role === "Member" || loginData?.is_police
      ? fetchScheduleForPolice(loginData?.organization?.uu_id as any)
      : fetchScheduleForCoordinator(loginData?.admin_unit as any),
  );
  // ScheduleList.tsx:40 — non-arrays become [].
  const rows: any[] = Array.isArray(data) ? data : [];

  // No tabs and no per-row navigation (edit/delete only) → list route.
  // Status mirrors the row badge logic (ScheduleList.tsx:186-192, :259-264).
  const now = new Date();
  const recent: RecentRow[] = rows.map((s: any, i) => {
    const startDate = new Date(s?.starting_date_time as any);
    const endDate = new Date(s?.end_date_time as any);
    const isActive = now >= startDate && now <= endDate;
    const isUpcoming = now < startDate;
    return {
      id: str(s?.id ?? i),
      title: str(s?.police_station_name),
      subtitle: s?.org_phone_number || undefined,
      status: isActive ? "active" : isUpcoming ? "upcoming" : "completed",
      date: dateStr(s?.starting_date_time),
      route: MODULE_ROUTES.schedules,
    };
  });

  return make("schedules", rows.length, [], recent);
}

// ---------------------------------------------------------------------------
// Asset recovery — AssetRecovery/AssetReferralList/AssetReferralList.tsx
// ---------------------------------------------------------------------------

// AssetReferralList.tsx:18-27 role tabs (literal labels).
const ASSET_REGISTRAR_TABS = ["New Request", "Registered", "Archived"];
const ASSET_PROSECUTOR_TABS = ["New Request", "On litigation", "Completed"];
const ASSET_DIRECTOR_TABS = [
  "New Request",
  "Assigned",
  "On Litigation",
  "Closed",
  "Terminated",
];
// AssetReferralList.tsx:30-66 statusMappings.
const ASSET_STATUS_MAPPINGS: Record<string, string[]> = {
  "New Request": ["Pending", "Forwarded to Registrar"],
  Registered: [
    "Prohibition Request Approved",
    "Asset Recovery Request Approved",
    "Court Case Filed",
  ],
  Archived: [
    "Recovered",
    "Closed by cordinator",
    "Closed by prosecutor",
    "Terminated By Prosecutor",
  ],
  "On litigation": [
    "Prohibition On litigation",
    "Asset Recovery On Litigation",
    "Court Case Filed",
  ],
  Completed: [
    "Recovered",
    "Closed by prosecutor",
    "Asset Recovery Final Verdict",
  ],
  Assigned: ["Assigned to Prosecutor", "Forwarded to Coordinator"],
  "On Litigation": [
    "Prohibition On litigation",
    "Asset Recovery On Litigation",
    "Court Case Filed",
  ],
  Closed: ["Recovered", "Closed by cordinator", "Closed by prosecutor"],
  Terminated: ["Terminated By Prosecutor", "Suspended"],
};

async function loadAssetRecovery(loginData: any): Promise<ModuleCount> {
  // Mirrors AssetReferralList.tsx:113-131.
  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const isAssetRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isDirector = hasRole(loginData?.roles, [Roles.director]);

  const roleTabs = isAssetRegistrar
    ? ASSET_REGISTRAR_TABS
    : isAssetProsecutor
      ? ASSET_PROSECUTOR_TABS
      : isAssetCoordinator
        ? ASSET_DIRECTOR_TABS
        : isDirector
          ? ASSET_DIRECTOR_TABS
          : []; // ["All"] fallback → no status tabs

  // Mirrors AssetReferralList.tsx:136-177.
  const data = await call(
    isAssetProsecutor
      ? fetchAssetReferralByProsecutorId(loginData as LoginAccount)
      : isAssetRegistrar
        ? fetchReferralsByAdminUnit(loginData?.admin_unit?.toString() as string)
        : fetchAssetReferralsByUnit(
            loginData?.admin_unit?.toString() as string,
            loginData?.org_unit?.toString() as string,
          ),
  );
  // Default tab "All" with no search/date/status filter (:180-224) keeps
  // every row, in API order.
  const rows = asArray(data);

  // Row click target: AssetReferralList.tsx:395.
  const recent: RecentRow[] = rows.map((r: any, i) => ({
    id: str(r?.id ?? i),
    title: str(r?.referral_id),
    subtitle: r?.receiving_office || r?.letter_number || undefined,
    status: r?.action_status ?? null,
    date: dateStr(r?.date),
    route: `/asset-recovery/${r?.id}`,
  }));

  return make(
    "assetRecovery",
    rows.length,
    roleTabs.map((tab) => {
      const statusesForTab = ASSET_STATUS_MAPPINGS[tab];
      return {
        key: tab,
        label: tab,
        count: statusesForTab
          ? rows.filter((referral: any) =>
              statusesForTab.includes(referral?.action_status as string),
            ).length
          : rows.length,
      };
    }),
    recent,
  );
}

// ---------------------------------------------------------------------------
// Asset appeals — AssetAppeal/ProsecutorAppeal/AssetAppealList.tsx and
// AssetAppeal/RespondentAppeal/RespondentAppealList.tsx
// ---------------------------------------------------------------------------

async function loadAssetAppeal(
  loginData: any,
  respondent: boolean,
): Promise<ModuleCount> {
  // Mirrors AssetAppealList.tsx:31,38-60 and RespondentAppealList.tsx:43-68.
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const data = await call(
    respondent
      ? isAssetProsecutor
        ? getRespondentAssetAppealsByProsecutor(
            loginData?.user_id?.toString() as string,
          )
        : getRespondentAssetAppealsByAdminUnit(
            loginData?.admin_unit?.toString() as string,
          )
      : isAssetProsecutor
        ? getAssetAppealsByProsecutor(loginData?.user_id?.toString() as string)
        : getAssetAppealsByAdminUnit(
            loginData?.admin_unit?.toString() as string,
          ),
  );
  // Both pages render the raw list (no filtering); only an "All" tab.
  const rows = asArray(data);

  // Row click targets: AssetAppealList.tsx:195 / RespondentAppealList.tsx:199.
  const base = respondent ? "/asset-appeal-respondent" : "/asset-appeal";
  const recent: RecentRow[] = rows.map((a: any, i) => ({
    id: str(a?.id ?? i),
    title: str(a?.asset_referral?.referral_id),
    subtitle: a?.appeal_type || undefined,
    status: a?.action_status ?? null,
    date: dateStr(a?.date_submitted),
    route: `${base}/${a?.id}`,
  }));

  return make(
    respondent ? "assetAppealRespondent" : "assetAppeal",
    rows.length,
    [],
    recent,
  );
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export async function loadModuleCount(
  key: ModuleKey,
  loginData: any,
): Promise<ModuleCount> {
  switch (key) {
    case "incidents":
      return loadIncidents(loginData);
    case "investigationFiles":
      return loadInvestigationFiles(loginData);
    case "furtherInvestigation":
      return loadFurtherInvestigation(loginData);
    case "charges":
      return loadCharges(loginData);
    case "upcomingHearings":
      return loadUpcomingHearings(loginData);
    case "prosecutorAppeal":
      return loadProsecutorAppeal(loginData);
    case "cassationAppeal":
      return loadCassationAppeal(loginData);
    case "defendantAppeal":
      // App.tsx:436 renders <DefendantAppealList /> (isCassation undefined).
      return loadDefendantAppeal(loginData, undefined);
    case "defendantCassation":
      // App.tsx:443-446 renders <DefendantAppealList isCassation />.
      return loadDefendantAppeal(loginData, true);
    case "bailRight":
      return loadBailRight(loginData);
    case "witnessProtection":
      return loadWitnessProtection(loginData);
    case "schedules":
      return loadSchedules(loginData);
    case "assetRecovery":
      return loadAssetRecovery(loginData);
    case "assetAppeal":
      return loadAssetAppeal(loginData, false);
    case "assetAppealRespondent":
      return loadAssetAppeal(loginData, true);
    default:
      throw new Error(`Unknown dashboard module: ${String(key)}`);
  }
}
